/**
 * «Телефония» (`/telephony`): внутренние номера АТС voip-2 ↔ сотрудники CRM и кто сейчас на линии (MicroSIP
 * подключён), тревоги — номер уволенного открыт, чей номер не знаем, сотрудник КЦ работал без номера;
 * «MicroSIP» (`?tab=microsip`) — как подключить; «Задачи робота» переехали в голосовой стенд
 * (`/voice-lab?tab=problems`), старая ссылка ведёт туда. Пока ARI не открыт, номера — снимок `telephony-sync` с отметкой времени.
 * Спека `docs/specs/2026-10-27-telephony-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { Navigate, useSearchParams } from "react-router";

import { type PbxRow, extensionsQuery } from "@/lib/telephony";

const ВКЛАДКИ = [
  ["numbers", "Номера"],
  ["microsip", "MicroSIP"],
] as const;

function Состояние({ r }: { r: PbxRow }) {
  if (r.closed) return <span className="text-fg-muted" title={r.closed}>закрыт</span>;
  if (r.state === "online") return <span className="font-medium text-ok">● на линии</span>;
  if (r.state === "offline") return <span className="text-fg-muted">не подключён</span>;
  return <span className="text-fg-muted">—</span>;
}

function Номера() {
  const q = useQuery({ ...extensionsQuery(), refetchInterval: 60000 });
  const d = q.data;
  if (q.isError) return <div className="text-err">Номера не загрузились</div>;
  if (!d) return <div className="text-fg-muted">Загружаю…</div>;
  return (
    <div className="flex flex-col gap-4">
      <div className="text-fg-muted">
        На линии сейчас: <b className="text-fg">{d.online}</b> · снимок АТС {d.synced_at?.slice(0, 16) ?? "—"}
        {d.source === "snapshot" && " (живое состояние — после доступа к ARI АТС)"}
      </div>
      {!!d.no_number.length && (
        <div className="text-err">
          Работали в КЦ за 7 дней, номера на АТС нет:{" "}
          {d.no_number.map((u) => `${u.name} (${u.hours} ч, звонков ${u.calls})`).join(", ")}
        </div>
      )}
      <table className="w-full max-w-5xl text-[12.5px]">
        <thead className="text-left text-fg-muted">
          <tr className="border-b border-border">
            <th className="py-1.5 pr-3 font-normal">Номер</th>
            <th className="pr-3 font-normal">Имя на АТС</th>
            <th className="pr-3 font-normal">Сотрудник CRM</th>
            <th className="pr-3 font-normal">За 7 дней</th>
            <th className="pr-3 font-normal">MicroSIP</th>
            <th className="font-normal">Внимание</th>
          </tr>
        </thead>
        <tbody>
          {d.rows.map((r) => (
            <tr key={r.number} className={`border-b border-border ${r.closed || r.service ? "opacity-60" : ""}`}>
              <td className="py-1.5 pr-3 tabular-nums">{r.number}</td>
              <td className="pr-3">{r.name}</td>
              <td className="pr-3">
                {r.user ? (
                  <>
                    {r.user.name}
                    {!r.user.active && <span className="text-err"> · уволен</span>}
                  </>
                ) : r.service ? (
                  <span className="text-fg-muted">служебный</span>
                ) : (
                  <span className="text-fg-muted">{r.known ?? "—"}</span>
                )}
              </td>
              <td className="pr-3 tabular-nums text-fg-muted">
                {r.user && (r.user.hours || r.user.calls) ? `${r.user.hours} ч · ${r.user.calls} зв.` : ""}
              </td>
              <td className="pr-3">
                <Состояние r={r} />
              </td>
              <td className="text-err">{r.alarm}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const КАРТИНКИ = "https://s.unirenter.ru/vs2311";

function MicroSip() {
  return (
    <div className="flex max-w-3xl flex-col gap-3 text-[13px] leading-relaxed" data-annot="инструкция MicroSIP">
      <p className="text-fg-muted">
        По материалам учебного центра: «настройка MicroSIP на сервер FreePBX» (№ 3737) и «обязательные настройки для
        стабильной работы звонков» (№ 4547).
      </p>
      <ol className="flex list-decimal flex-col gap-2 pl-5">
        <li>
          Скачайте и установите MicroSIP:{" "}
          <a href="https://www.microsip.org/downloads" target="_blank" rel="noreferrer" className="text-accent hover:underline">
            microsip.org/downloads
          </a>
          .
        </li>
        <li>
          Меню → «Добавить аккаунт». <b>SIP-сервер</b> и <b>Домен</b>: <code>voip-2.unirenter.ru:5160</code>.
        </li>
        <li>
          <b>Имя пользователя</b> и <b>Логин</b> — ваш внутренний номер (четыре цифры, его выдаёт руководитель),{" "}
          <b>Пароль</b> — выданный вместе с номером. Пароль никому не пересылайте и не записывайте в чаты.{" "}
          <a href={`${КАРТИНКИ}/Q043L5qZh7K0c17wFA5D.png`} target="_blank" rel="noreferrer" className="text-accent hover:underline">
            пример
          </a>
        </li>
        <li>
          Обязательные настройки аккаунта: <b>Транспорт</b> — UDP + TCP; <b>Обновление регистрации</b> — 60; галки
          «Публиковать присутствие» и «Разрешить перезапись IP».{" "}
          <a href={`${КАРТИНКИ}/sQft88NQnAAIZ4CTPqgA.png`} target="_blank" rel="noreferrer" className="text-accent hover:underline">
            как выглядит
          </a>
        </li>
        <li>
          Всё верно — значок станет зелёным, а на вкладке «Номера» напротив вас появится «● на линии» (после следующего
          снимка АТС).{" "}
          <a href={`${КАРТИНКИ}/uArIEpJI6o19oMvLNmdy.png`} target="_blank" rel="noreferrer" className="text-accent hover:underline">
            зелёный значок
          </a>
        </li>
        <li>
          Не подключается — включите VPN и укажите внутренний адрес <code>192.168.81.225:5160</code>.
        </li>
      </ol>
      <p className="text-fg-muted">
        Нет номера или уволился сотрудник — напишите руководителю: номер уволенного закрывают (новый пароль, вход
        только с самой АТС), чтобы с него никто не мог звонить.
      </p>
    </div>
  );
}

export function TelephonyPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") ?? "numbers";
  if (tab === "problems") return <Navigate to="/voice-lab?tab=problems" replace />;
  return (
    <section className="flex flex-col gap-4 p-6 text-[12.5px]" data-annot="телефония">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="mr-2 text-[16px] font-semibold">Телефония</h1>
        {ВКЛАДКИ.map(([к, имя]) => (
          <button
            key={к}
            type="button"
            onClick={() => setParams(к === "numbers" ? {} : { tab: к })}
            className={`rounded-full border px-3 py-1 text-[12.5px] ${tab === к ? "border-accent text-accent" : "border-border text-fg-muted hover:text-fg"}`}
          >
            {имя}
          </button>
        ))}
      </div>
      {tab === "microsip" ? <MicroSip /> : <Номера />}
    </section>
  );
}
